import { useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import {
  Activity,
  CalendarDays,
  ClipboardList,
  DoorOpen,
  FileBarChart2,
  LayoutDashboard,
  ListOrdered,
  LogOut,
  Menu,
  Monitor,
  Settings,
  ShieldCheck,
  Stethoscope,
  Users,
  UsersRound,
  X,
  type LucideIcon,
} from 'lucide-react';
import { UserRole } from '@docsystem/shared';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useLogout, useSession } from '@/features/auth/useSession';
import { cn, initials } from '@/lib/utils';

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
}

const NAV: Record<string, NavItem[]> = {
  [UserRole.ADMIN]: [
    { to: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { to: '/admin/queues', label: 'Queues', icon: ListOrdered },
    { to: '/admin/appointments', label: 'Appointments', icon: CalendarDays },
    { to: '/admin/patients', label: 'Patients', icon: UsersRound },
    { to: '/admin/doctors', label: 'Doctors', icon: Stethoscope },
    { to: '/admin/chambers', label: 'Chambers', icon: DoorOpen },
    { to: '/admin/schedules', label: 'Schedules', icon: ClipboardList },
    { to: '/admin/displays', label: 'Displays', icon: Monitor },
    { to: '/admin/users', label: 'Users', icon: Users },
    { to: '/admin/reports', label: 'Reports', icon: FileBarChart2 },
    { to: '/admin/audit-logs', label: 'Audit Logs', icon: ShieldCheck },
    { to: '/admin/settings', label: 'Settings', icon: Settings },
  ],
  [UserRole.RECEPTIONIST]: [
    { to: '/reception/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { to: '/reception/queue', label: 'Queues', icon: ListOrdered },
    { to: '/reception/appointments', label: 'Appointments', icon: CalendarDays },
    { to: '/reception/patients', label: 'Patients', icon: UsersRound },
  ],
  [UserRole.DOCTOR]: [
    { to: '/doctor/dashboard', label: 'My Queue', icon: Activity },
    { to: '/doctor/appointments', label: "Today's List", icon: CalendarDays },
  ],
};

export function AppShell() {
  const { user, clinic, role } = useSession();
  const logout = useLogout();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const items = role ? (NAV[role] ?? []) : [];

  /*
   * Focused surfaces hide the chrome.
   *
   * The consultation screen is the one place in this app where the doctor is with a
   * patient and the screen IS the task. A nav rail there costs 240px of the width the
   * prescription needs and offers, mid-note, one thing: somewhere else to go. The
   * note's own header carries the way back, so nothing is stranded.
   *
   * Only from `lg` up, where the lock is a layout decision rather than a trap: below
   * that the shell keeps its normal scrolling height.
   */
  const focused = /\/consultation\//.test(location.pathname);

  return (
    <div
      className={cn(
        'flex bg-muted/30',
        focused ? 'min-h-screen lg:h-dvh lg:overflow-hidden' : 'min-h-screen',
      )}
    >
      {/* Sidebar — desktop */}
      <aside
        className={cn(
          'w-60 shrink-0 flex-col border-r bg-card',
          focused ? 'hidden' : 'hidden lg:flex',
        )}
      >
        <Brand />
        <nav className="flex-1 space-y-0.5 overflow-y-auto p-3 scrollbar-thin">
          {items.map((item) => (
            <NavItemLink key={item.to} item={item} />
          ))}
        </nav>
        <UserMenu onLogout={() => void logout()} />
      </aside>

      {/* Sidebar — mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setMobileOpen(false)}
            aria-hidden
          />
          <aside className="absolute left-0 top-0 flex h-full w-64 flex-col bg-card shadow-xl">
            <div className="flex items-center justify-between border-b px-4 py-3">
              <Brand compact />
              <Button
                size="icon"
                variant="ghost"
                onClick={() => setMobileOpen(false)}
                aria-label="Close navigation"
              >
                <X />
              </Button>
            </div>
            <nav className="flex-1 space-y-0.5 overflow-y-auto p-3">
              {items.map((item) => (
                <NavItemLink key={item.to} item={item} onNavigate={() => setMobileOpen(false)} />
              ))}
            </nav>
            <UserMenu onLogout={() => void logout()} />
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex items-center gap-3 border-b bg-card px-4 py-2.5 lg:hidden">
          <Button
            size="icon"
            variant="ghost"
            onClick={() => setMobileOpen(true)}
            aria-label="Open navigation"
          >
            <Menu />
          </Button>
          <span className="truncate text-sm font-semibold">
            {items.find((i) => location.pathname.startsWith(i.to))?.label ?? clinic?.name}
          </span>
          <div className="ml-auto flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
            {user ? initials(user.fullName) : '—'}
          </div>
        </header>

        <main
          className={cn(
            'flex min-w-0 flex-1 flex-col',
            // A focused surface sizes itself to the viewport, so the padding that
            // frames an ordinary page would become a border it has to subtract back
            // out with a calc(). Dropping it here is what lets the page just be h-full.
            focused ? 'lg:min-h-0' : 'p-4 sm:p-6',
          )}
        >
          <Outlet />
        </main>
      </div>
    </div>
  );
}

/**
 * The logo carries the branding on its own — it already spells out the clinic name, so
 * repeating it beside the mark was saying the same thing twice and squeezing the image
 * into a 36px crop to make room. Given the full sidebar width the whole lockup is
 * legible, so `object-contain` now shows all of it.
 *
 * `alt` names the destination rather than the picture, because the mark is wrapped in
 * the link home; a screen reader announcing "logo" there would be useless.
 */
function Brand({ compact }: { compact?: boolean }) {
  const { clinic } = useSession();

  return (
    <div className={cn('flex items-center', compact ? '' : 'border-b px-3 py-4')}>
      <img
        // Behind the sign-in, unlike the login screens: the session carries the
        // clinic's own logoUrl, so the shell follows whatever the clinic record
        // says rather than pinning one tenant's file into the source. The fallback
        // covers a clinic that has not uploaded a mark.
        src={clinic?.logoUrl ?? '/totall-logo.png'}
        alt="Clinic home"
        className={cn('w-auto object-contain', compact ? 'h-10 max-w-[12rem]' : 'h-16 max-w-full')}
      />
    </div>
  );
}

function NavItemLink({ item, onNavigate }: { item: NavItem; onNavigate?: () => void }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
          isActive
            ? 'bg-primary/10 text-primary'
            : 'text-muted-foreground hover:bg-muted hover:text-foreground',
        )
      }
    >
      <Icon className="h-4 w-4 shrink-0" aria-hidden />
      <span className="truncate">{item.label}</span>
    </NavLink>
  );
}

function UserMenu({ onLogout }: { onLogout: () => void }) {
  const { user } = useSession();
  if (!user) return null;

  return (
    <div className="border-t p-3">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="flex w-full items-center gap-2.5 rounded-lg px-2 py-2 text-left transition-colors hover:bg-muted">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
              {initials(user.fullName)}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium leading-tight">{user.fullName}</p>
              <p className="truncate text-[11px] capitalize text-muted-foreground">
                {user.role.toLowerCase()}
              </p>
            </div>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-52">
          <DropdownMenuLabel className="font-normal">
            <p className="text-sm font-medium">{user.fullName}</p>
            <p className="truncate text-xs text-muted-foreground">{user.email}</p>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem destructive onClick={onLogout}>
            <LogOut />
            Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
